import { HTMLProps, MouseEventHandler, ReactNode } from 'react';

import { getDOMProps } from 'app/shared/utils/react';
import styled, { css } from 'styled-components';

import { Drawer } from '@mui/material';
import { IconX } from '@u21/tabler-icons';
import {
  U21Button,
  U21ButtonProps,
} from 'app/shared/u21-ui/components/input/U21Button';
import { U21Divider } from 'app/shared/u21-ui/components/display/U21Divider';
import { U21Spacer } from 'app/shared/u21-ui/components/layout/U21Spacer';
import { U21Typography } from 'app/shared/u21-ui/components/display/typography/U21Typography';

const SIZES = {
  SMALL: 'small',
  MEDIUM: 'medium',
} as const;

export type U21DrawerSize = ValueOf<typeof SIZES>;

export interface U21DrawerProps extends Omit<
  HTMLProps<HTMLDivElement>,
  'size' | 'title'
> {
  actionButtonProps?: U21ButtonProps;
  children: ReactNode;
  closeButtonProps?: U21ButtonProps;
  noPadding?: boolean;
  onAction?: MouseEventHandler<HTMLButtonElement>;
  onClose: MouseEventHandler<HTMLButtonElement>;
  onEnter?: () => void;
  onExited?: () => void;
  open: boolean;
  size?: U21DrawerSize;
  title: ReactNode;
}

export const U21Drawer = (props: U21DrawerProps) => {
  const {
    actionButtonProps,
    children,
    closeButtonProps,
    noPadding,
    onAction,
    onClose,
    onEnter,
    onExited,
    open,
    size = SIZES.MEDIUM,
    title,
    ...rest
  } = props;
  return (
    <Drawer
      anchor="right"
      hideBackdrop
      onClose={onClose}
      SlideProps={{ onEnter, onExited }}
      open={open}
      variant="persistent"
    >
      <Container $size={size} {...getDOMProps(rest)}>
        <Header>
          <U21Typography variant="h6">{title}</U21Typography>
          <StyledU21Button
            aria-label="close"
            icon={<IconX />}
            onClick={onClose}
          />
        </Header>
        <StyledU21Divider horizontal />
        <Content $noPadding={noPadding}>{children}</Content>
        <StyledU21Divider horizontal />
        <Footer horizontal>
          <U21Button
            // eslint-disable-next-line react/no-children-prop
            children="Close"
            {...closeButtonProps}
            onClick={onClose}
          />
          {(Boolean(onAction) || Boolean(actionButtonProps)) && (
            <U21Button
              // eslint-disable-next-line react/no-children-prop
              children="Confirm"
              color="primary"
              variant="contained"
              {...actionButtonProps}
              onClick={onAction}
            />
          )}
        </Footer>
      </Container>
    </Drawer>
  );
};

const Container = styled.div<{ $size: U21DrawerSize }>`
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  height: 100%;

  ${(props) => {
    switch (props.$size) {
      case SIZES.SMALL:
        return css`
          width: 370px;
        `;
      case SIZES.MEDIUM:
        return css`
          width: 560px;
        `;
      default:
        return css``;
    }
  }}
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
`;

const StyledU21Divider = styled(U21Divider)`
  margin: 0 16px;
`;

const Content = styled.div<{ $noPadding?: boolean }>`
  flex: 1 1 0;
  overflow: auto;

  ${(props) =>
    props.$noPadding
      ? css``
      : css`
          // 0 padding top since Header takes care of the padding
          padding: 0 16px 16px;
        `}
`;

const Footer = styled(U21Spacer)`
  padding: 16px;
  justify-content: flex-end;
`;

const StyledU21Button = styled(U21Button)`
  margin-left: auto;
`;
